import { motion } from "framer-motion";
import { MapPin, Phone, MessageCircle, MoonStar, Navigation } from "lucide-react";
import { BUSINESS, waLink } from "@/lib/site";
import { StarField } from "@/components/StarField";
import { MoonPhases } from "@/components/MoonPhases";
import { GoldDivider } from "@/components/GoldDivider";
import { useLang } from "@/i18n";

export const Hero = () => {
    const { t } = useLang();
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(BUSINESS.mapsQuery)}`;

    const ctas = [
        { label: t("hero.ctaCall"), href: `tel:${BUSINESS.phoneTel}`, icon: Phone, primary: true, testid: "hero-call-button", external: false },
        { label: t("hero.ctaBook"), href: waLink(t("wa.book")), icon: MoonStar, primary: false, testid: "hero-book-button", external: true },
        { label: t("hero.ctaMessage"), href: waLink(t("wa.message")), icon: MessageCircle, primary: false, testid: "hero-message-button", external: true },
        { label: t("hero.ctaDirections"), href: mapsUrl, icon: Navigation, primary: false, testid: "hero-directions-button", external: true },
    ];

    return (
        <section id="inicio" data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
            <div
                className="absolute inset-0 bg-cover"
                style={{ backgroundImage: "url(/images/altar.jpg)", backgroundPosition: "center 22%", filter: "brightness(0.72) saturate(1.05)" }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-ink/45" aria-hidden="true" />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "linear-gradient(to bottom, rgba(15,13,17,0.88) 0%, rgba(15,13,17,0.45) 32%, rgba(15,13,17,0.45) 62%, rgba(15,13,17,0.92) 100%), radial-gradient(ellipse 72% 58% at 50% 54%, rgba(15,13,17,0.55) 0%, transparent 75%)",
                }}
                aria-hidden="true"
            />
            <div
                className="absolute inset-0 candle-glow"
                style={{
                    background:
                        "radial-gradient(ellipse 60% 45% at 50% 62%, rgba(212,175,55,0.14) 0%, transparent 70%), radial-gradient(ellipse 80% 60% at 50% 110%, rgba(74,14,28,0.5) 0%, transparent 60%)",
                }}
                aria-hidden="true"
            />
            <StarField count={70} />
            <div className="absolute top-0 left-0 right-0 h-40 section-fade-top" aria-hidden="true" />

            <div className="relative z-10 text-center px-5 py-32 max-w-4xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="flex justify-center mb-7"
                >
                    <MoonPhases size={13} />
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.25 }}
                    className="font-accent text-[11px] sm:text-xs tracking-[0.4em] uppercase text-gold-soft mb-6"
                >
                    {t("hero.eyebrow")}
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{ duration: 0.9, delay: 0.35 }}
                    className="font-display font-semibold gold-text text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-wide"
                >
                    Yerberia Jardin D'Venuz
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.55 }}
                    className="font-display italic text-xl sm:text-2xl text-cream/90 mt-5"
                >
                    {t("hero.subtitle")}
                </motion.p>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.65 }}
                    className="font-accent text-[10px] sm:text-[11px] tracking-[0.25em] uppercase text-gold/85 mt-6"
                    data-testid="hero-services-line"
                >
                    {t("hero.servicesLine")}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.75 }}
                >
                    <GoldDivider className="my-6" />
                    <p className="text-sm sm:text-base text-cream/75 max-w-xl mx-auto font-light leading-relaxed">
                        {t("hero.desc")}
                    </p>
                    <p className="text-xs sm:text-sm text-gold-soft/90 tracking-wide mt-3">
                        {t("hero.availability")}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.9 }}
                    className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-9 max-w-3xl mx-auto"
                    data-testid="hero-cta-group"
                >
                    {ctas.map((c) => {
                        const Icon = c.icon;
                        return (
                            <a
                                key={c.testid}
                                href={c.href}
                                {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                data-testid={c.testid}
                                className={`inline-flex items-center justify-center gap-2 font-accent text-[10px] sm:text-[11px] tracking-[0.16em] uppercase px-4 py-3.5 rounded-sm hover:-translate-y-0.5 transition-[box-shadow,transform,filter,background-color] duration-300 ${
                                    c.primary
                                        ? "bg-gold text-ink font-bold hover:brightness-110 hover:shadow-[0_0_28px_rgba(212,175,55,0.4)]"
                                        : "border border-gold/60 text-gold hover:bg-gold/10 hover:shadow-[0_0_22px_rgba(212,175,55,0.2)]"
                                }`}
                            >
                                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.8} />
                                {c.label}
                            </a>
                        );
                    })}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.05 }}
                    className="mt-7"
                >
                    <a
                        href="#velas"
                        data-testid="hero-candle-link"
                        className="font-display italic text-base sm:text-lg text-gold-soft hover:text-gold underline decoration-gold/30 underline-offset-4 hover:decoration-gold/70 transition-colors duration-300"
                    >
                        {t("hero.candleLink")}
                    </a>
                    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-xs text-sand mt-5">
                        <span className="inline-flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-gold/70" />
                            {BUSINESS.address}
                        </span>
                        <a
                            href={`tel:${BUSINESS.phoneTel}`}
                            data-testid="hero-phone-link"
                            className="inline-flex items-center gap-1.5 hover:text-gold transition-colors duration-300"
                        >
                            <Phone className="w-3.5 h-3.5 text-gold/70" />
                            {BUSINESS.phoneDisplay}
                        </a>
                    </div>
                </motion.div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-36 section-fade-bottom" aria-hidden="true" />
        </section>
    );
};
